import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { format, subDays } from 'date-fns'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SendIcon from '@mui/icons-material/Send'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import MarkChatReadOutlinedIcon from '@mui/icons-material/MarkChatReadOutlined'
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined'
import KPICard from '../components/KPICard'
import InterventionFilters from '../components/InterventionFilters'
import TrendChart from '../components/TrendChart'
import CallOutcomeChart from '../components/CallOutcomeChart'
import MerchantBreakdownTable from '../components/MerchantBreakdownTable'
import { MOCK_WHATSAPP } from '../mockData'
import { useGetWhatsappAnalyticsQuery } from '../../../redux/interventionApiSlice'

const USE_MOCK = false

export default function WhatsappAnalytics() {
  const [filters, setFilters] = useState({
    type: 'BOTH',
    merchantOids: [],
    dateRange: { from: format(subDays(new Date(), 2), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  })

  const noMerchant = !USE_MOCK && !filters.merchantOids?.length
  const { data: apiData, isFetching } = useGetWhatsappAnalyticsQuery(filters, { skip: USE_MOCK || noMerchant })
  const data = USE_MOCK ? MOCK_WHATSAPP : apiData
  const isLoading = USE_MOCK ? false : isFetching

  const merchantCols = [
    { key: 'merchantName', label: 'Merchant', format: 'text' },
    { key: 'whatsappSent', label: 'Messages Sent', format: 'number', align: 'right' },
    { key: 'uniqueAwbs', label: 'Unique AWBs', format: 'number', align: 'right' },
    { key: 'engaged', label: 'Engaged', format: 'number', align: 'right' },
    { key: 'engagementRate', label: 'Engagement %', format: 'percent', align: 'right' },
  ]

  // Per-merchant engagement rate, same basis as the top-level KPI (engaged / messages sent).
  const merchantRows = (data?.merchantBreakdown || []).map((m) => ({
    ...m,
    engagementRate: m.whatsappSent ? (m.engaged / m.whatsappSent) * 100 : null,
  }))

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>WhatsApp Analytics</Typography>
      <InterventionFilters filters={filters} onChange={setFilters} />

      {noMerchant ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant='body1' sx={{ color: 'text.disabled' }}>Select a merchant to view data</Typography>
        </Box>
      ) : <>

      <Box className='kpi-row'>
        <KPICard title='Total Sent' value={data?.totalSent} icon={<WhatsAppIcon />} color='#25D366' loading={isLoading} />
        <KPICard title='Unique AWBs' value={data?.uniqueAwbs} icon={<Inventory2OutlinedIcon />} color='#7B1FA2' loading={isLoading} />
        <KPICard title='NDR Messages' value={data?.templateBreakdown?.find(t => (t.displayName || '').includes('NDR'))?.count} icon={<SendIcon />} color='#0F4C81' loading={isLoading} />
        <KPICard title='NPR Messages' value={data?.templateBreakdown?.find(t => (t.displayName || '').includes('NPR'))?.count} icon={<SendIcon />} color='#E85D3A' loading={isLoading} />
      </Box>

      {/* Engagement — customers who actually replied to the WhatsApp interaction */}
      <Box className='kpi-row'>
        <KPICard title='Engaged Conversations' value={data?.engagement?.engagedConversations} icon={<QuestionAnswerOutlinedIcon />} color='#1976D2' loading={isLoading} />
        <KPICard title='Engaged AWBs' value={data?.engagement?.engagedAwbs} icon={<MarkChatReadOutlinedIcon />} color='#0F4C81' loading={isLoading} />
        <KPICard title='Engagement Rate' value={data?.engagement?.engagementRate} format='percent' icon={<PercentOutlinedIcon />} color='#2E7D32' loading={isLoading} />
      </Box>

      <TrendChart
        data={data?.dailyTrend || []}
        title='Daily WhatsApp Activity'
        series={[
          { key: 'whatsappSent', label: 'Messages Sent', color: '#25D366' },
          { key: 'uniqueAwbs', label: 'Unique AWBs', color: '#0F4C81' },
          { key: 'engaged', label: 'Engaged', color: '#E85D3A' },
        ]}
        loading={isLoading}
      />

      {/* What engaged customers chose — reattempt / RTO / no terminal action */}
      <Box sx={{ mt: 2 }}>
        <CallOutcomeChart data={data?.outcomes || {}} title='Engagement Outcomes' loading={isLoading} />
      </Box>

      {/* Template Breakdown Table */}
      <Paper sx={{ p: 2.5, mt: 2 }}>
        <Typography variant='subtitle1' sx={{ mb: 1.5 }}>Template Breakdown</Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell align='right'>Count</TableCell>
                <TableCell align='right'>% of Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.templateBreakdown || []).map(t => (
                <TableRow key={t.templateName} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.templateName}</TableCell>
                  <TableCell>{t.displayName}</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>{t.count?.toLocaleString('en-IN')}</TableCell>
                  <TableCell align='right'>{data?.totalSent ? `${(t.count / data.totalSent * 100).toFixed(1)}%` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <MerchantBreakdownTable
          data={merchantRows}
          columns={merchantCols}
          title='Messages by Merchant'
          loading={isLoading}
          defaultSort={{ field: 'whatsappSent', direction: 'desc' }}
        />
      </Box>
      </>}
    </Box>
  )
}
